import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";

export default function Categories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#C85A40" });

  const load = async () => {
    const { data } = await api.get("/categories");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", color: "#C85A40" });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, color: c.color || "#C85A40" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/categories/${editing.id}`, form);
        toast.success("Categoría actualizada");
      } else {
        await api.post("/categories", form);
        toast.success("Categoría creada");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Error");
    }
  };

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.name}"?`)) return;
    await api.delete(`/categories/${c.id}`);
    toast.success("Eliminada");
    load();
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="categories-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-clay font-semibold">Organización</div>
          <h1 className="font-outfit text-4xl font-bold text-stone">Categorías</h1>
        </div>
        <Button
          onClick={openNew}
          data-testid="category-new-button"
          className="bg-clay hover:bg-clay-hover text-white shadow-[0_4px_14px_rgba(200,90,64,0.39)] hover:-translate-y-0.5 transition-all"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva categoría
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div
            key={c.id}
            className="bg-white rounded-xl border border-warm-border p-5 shadow-sm flex items-center justify-between hover:-translate-y-0.5 transition-transform"
            data-testid={`category-card-${c.id}`}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `${c.color}20`, color: c.color }}
              >
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <div className="font-outfit font-semibold text-stone">{c.name}</div>
                <div className="text-xs text-stone/50 uppercase tracking-wider">Categoría</div>
              </div>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => openEdit(c)}
                className="p-2 rounded-md hover:bg-sand text-stone/60 hover:text-clay"
                data-testid={`category-edit-${c.id}`}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => del(c)}
                className="p-2 rounded-md hover:bg-clay/10 text-stone/60 hover:text-clay"
                data-testid={`category-delete-${c.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-stone/50 py-12 bg-white rounded-xl border border-warm-border">
            Aún no hay categorías.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-bone border-warm-border">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl">
              {editing ? "Editar categoría" : "Nueva categoría"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-1.5 block">
                Nombre
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="category-name-input"
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-1.5 block">
                Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-12 h-10 rounded-lg cursor-pointer border border-warm-border"
                />
                <Input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="border-warm-border bg-white font-mono uppercase focus-visible:ring-clay focus-visible:ring-offset-0"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-warm-border">
              Cancelar
            </Button>
            <Button
              onClick={save}
              data-testid="category-save-button"
              className="bg-clay hover:bg-clay-hover text-white"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
